import type { FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  DataGridPremiumProps,
} from "@mui/x-data-grid-premium";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import { Checkbox } from "@privacy/ui";
import IconButton from "@mui/material/IconButton";
import { Navigate, useParams } from "react-router";
import { DynamicShared } from "../../../dynamicShared";
import { ChevronRightIcon } from "../../../shared/icons";
import { Grid } from "../../../shared/Grid";
import type { TemplateFormQuestion } from "../../../api/templateFormSettings.types";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { HoverPopover, useHoverPopover } from "../../../shared/HoverPopover";
import { multilineEllipsis } from "../../../shared/multilineEllipsis";
import { useTemplateFormSectionsState } from "./TemplateFormProvider";
import { TEMPLATE_FROM_DRAWER_WIDTH, TemplateFormDrawer } from "./TemplateFormDrawer";
import type { TemplateFormQuestionRow } from "./TemplateFormDialogContent";
import { DIALOG_HEADER_HEIGHT, DIALOG_FOOTER_HEIGHT, getInputTypeInfo } from "./utils";

const CHECKBOX_FIELD = "checkBox";

export const TemplateFormSectionsGrid: FC<{ questionRows: TemplateFormQuestionRow[]; formId: string }> = ({
  questionRows,
  formId,
}) => {
  const { t } = useTranslation();
  const params = useParams<{ questionId: string | undefined }>();
  const { navigate } = usePrivacyNavigate();

  const apiRef = useGridApiRef();

  const { pendingChanges, addPendingChanges } = useTemplateFormSectionsState();
  const { popoverProps, anchorProps } = useHoverPopover();

  let selectedQuestionIds = questionRows.filter(q => q.enabled).map(q => q.id);

  if (pendingChanges.length > 0) {
    const questionToDisable = pendingChanges.filter(q => !q.enabled);
    const questionRowsToEnable = pendingChanges.filter(q => q.enabled);

    const questionIdsToDisable = questionToDisable.map(q => q.id);
    const questionIdsToEnable = questionRowsToEnable.map(q => q.id);

    // Uncheck questionRows
    selectedQuestionIds = selectedQuestionIds.filter(id => !questionIdsToDisable.includes(id));

    // Check questionRows
    selectedQuestionIds.push(...questionIdsToEnable);
  }

  const isAllParentSelected = (question: TemplateFormQuestionRow) => {
    const parentQuestionIds = getParentQuestionIds(question, questionRows);

    return parentQuestionIds.every(id => selectedQuestionIds.includes(id));
  };

  const handleRowSelection = (newSelection: GridRowSelectionModel) => {
    const [questionId] = Array.from(newSelection.ids);
    if (questionId && questionId !== params.questionId)
      navigate(String(params.questionId ? `../${questionId}` : questionId));
  };
  const rowSelectionModel: GridRowSelectionModel = {
    type: "include",
    ids: params.questionId ? new Set([params.questionId]) : new Set(),
  };

  const handleSelection = (row: TemplateFormQuestionRow, checked: boolean) => {
    const question = questionRows.find(q => q.id === row.id);

    if (!question) return;

    let questionIds: string[] = [];

    if (checked) {
      // In case if we check the root question, then we need to get all the follow up questionRows and check them as well.
      if (!question.parentId) {
        const followUpIds = getFollowUpQuestionIds(question);

        questionIds = [question.id, ...followUpIds];
      } else {
        // If we check followup question, then we need to check the parent questionRows as well.
        const parentQuestionIds: string[] = getParentQuestionIds(question, questionRows);

        questionIds = [question.id, ...parentQuestionIds];
      }
    } else {
      questionIds = [question.id];
    }

    // Get question ids which is modified but not included in pending changes.
    const nonPendingQuestionIds = questionIds.filter(
      id => !pendingChanges.some(pc => pc.id === id) && questionRows.some(q => q.id == id && q.enabled !== checked)
    );

    if (nonPendingQuestionIds.length > 0) {
      // In case if there is any pending changes which is not yet added, then we add them.
      const pendingquestionRows = nonPendingQuestionIds.map(id => {
        return { id, enabled: checked };
      });

      addPendingChanges([...pendingChanges, ...pendingquestionRows]);
    } else {
      // Otherwise we filter out pending changes, since question data is changed to the initial state.
      const filteredPendingquestionRows = pendingChanges.filter(pc => !questionIds.includes(pc.id));

      addPendingChanges([...filteredPendingquestionRows]);
    }
  };

  const groupingColDef: DataGridPremiumProps["groupingColDef"] = {
    headerName: t("TemplatePage.GridHeader.QuestionText"),
    renderCell: CustomGridTreeDataGroupingCell,
    width: 570,
  };

  const columns: GridColDef<TemplateFormQuestionRow>[] = [
    {
      field: CHECKBOX_FIELD,
      headerName: t("TemplatePage.GridHeader.CheckBox"),
      editable: false,
      sortable: false,
      width: 70,
      renderCell: ({ row }: { row: TemplateFormQuestionRow }) => {
        if (row.enabled && row.mandatory) {
          return (
            <>
              <HoverPopover {...popoverProps}>
                <Stack spacing={1} sx={{ p: 4, maxWidth: "20rem" }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                    {t("TemplatePage.MandatoryQuestion.Header")}
                  </Typography>

                  <Typography variant="body2" sx={{ fontSize: theme => theme.typography.pxToRem(12) }}>
                    {t("TemplatePage.MandatoryQuestion.Body")}
                  </Typography>
                </Stack>
              </HoverPopover>
              <Stack
                component="label"
                {...anchorProps}
                sx={{
                  py: 2,
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Checkbox checked disabled size="small" {...anchorProps} />
              </Stack>
            </>
          );
        }

        return (
          <Stack component="label" sx={{ py: 2, justifyContent: "center", width: "100%" }}>
            <Checkbox
              sx={{ "&:before": { content: "''", position: "absolute", inset: 0, transform: "scale(1.4)" } }}
              checked={selectedQuestionIds.includes(row.id)}
              onClick={e => e.stopPropagation()}
              onChange={(_, checked) => handleSelection(row, checked)}
              disabled={Boolean(row.parentId) && !isAllParentSelected(row)}
              size="small"
            />
          </Stack>
        );
      },
    },
    {
      field: "instructionText",
      headerName: t("TemplatePage.GridHeader.InstructionText"),
      editable: false,
      sortable: false,
      width: 300,
      renderCell: ({ row }) => (
        <Typography sx={{ ...multilineEllipsis(2), fontSize: theme => theme.typography.pxToRem(14) }}>
          {row.customizedFields?.description ?? row.description}
        </Typography>
      ),
    },
    {
      field: "questionType",
      headerName: t("TemplatePage.GridHeader.QuestionType"),
      editable: false,
      sortable: false,
      width: 200,
      renderCell: ({ row }) => {
        const inputTypeInfo = getInputTypeInfo(row.inputType);
        if (!inputTypeInfo) return null;
        return <DynamicShared.Chip label={t(inputTypeInfo.textKey)} variant="choice" />;
      },
    },
    {
      field: "helpInformation",
      headerName: t("TemplatePage.GridHeader.HelpInformation"),
      editable: false,
      sortable: false,
      width: 300,
      renderCell: ({ row }) => (
        <Typography sx={{ ...multilineEllipsis(2), fontSize: theme => theme.typography.pxToRem(14) }}>
          {row.helpInformation}
        </Typography>
      ),
    },
    {
      field: "extraHelpInformation",
      headerName: t("TemplatePage.GridHeader.ExtraHelpInformation"),
      editable: false,
      sortable: false,
      width: 300,
      renderCell: ({ row }) => (
        <Typography sx={{ ...multilineEllipsis(2), fontSize: theme => theme.typography.pxToRem(14) }}>
          {row.customizedFields?.extraHelpInformation}
        </Typography>
      ),
    },
  ];

  return (
    <Stack direction="row" sx={{ width: `calc(100% - ${params.questionId ? TEMPLATE_FROM_DRAWER_WIDTH : 0})` }}>
      <Grid
        sx={{ height: `calc(100vh - ${DIALOG_FOOTER_HEIGHT} - ${DIALOG_HEADER_HEIGHT} )` }}
        disableColumnMenu
        apiRef={apiRef}
        hideFooter
        getRowHeight={() => "auto"}
        disableMultipleRowSelection
        disableRowSelectionExcludeModel
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelection}
        defaultGroupingExpansionDepth={-1}
        treeData
        getTreeDataPath={getTreeDataPath}
        disableColumnReorder
        groupingColDef={groupingColDef}
        initialState={{ pinnedColumns: { left: [CHECKBOX_FIELD] } }}
        rows={questionRows}
        partsSx={{ mainSx: { border: "none" } }}
        columns={columns}
      />
      {(() => {
        if (!params.questionId) return null;
        const questionRow = questionRows.find(({ id }) => id === params.questionId);
        if (!questionRow) return <Navigate to=".." replace />;
        return (
          <TemplateFormDrawer
            onClose={() => navigate("..")}
            formId={formId}
            key={questionRow.id}
            question={questionRow}
          />
        );
      })()}
    </Stack>
  );
};

const getTreeDataPath = (row: TemplateFormQuestionRow) => row.path;

const getFollowUpQuestionIds = (question: TemplateFormQuestion): string[] => {
  const ids: string[] = [];

  if (question.followUps !== undefined && question.followUps.length > 0) {
    question.followUps.forEach(followUp => {
      ids.push(followUp.id);

      ids.push(...getFollowUpQuestionIds(followUp));
    });
  }

  return ids;
};

const getParentQuestionIds = (
  question: TemplateFormQuestionRow,
  questionRows: TemplateFormQuestionRow[],
  parentQuestionRowIds: string[] = []
): string[] => {
  if (!question.parentId) return parentQuestionRowIds;

  const parentQuestion = questionRows.find(q => q.id === question.parentId);

  if (parentQuestion) {
    parentQuestionRowIds.push(parentQuestion.id);

    return getParentQuestionIds(parentQuestion, questionRows, parentQuestionRowIds);
  }

  return parentQuestionRowIds;
};

function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
  // link to solution
  // https://stackoverflow.com/questionRows/76846689/datagridpro-modify-treegrid-node-icon-based-on-data
  const { id, field, rowNode, row } = props;
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);

  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;
  const isExpanded = rowNode.type === "group" && rowNode.childrenExpanded;

  const handleClick = (event: { stopPropagation: () => void }) => {
    if (rowNode.type !== "group") {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  return (
    <Box sx={{ ml: 5, width: "100%" }}>
      {filteredDescendantCount > 0 ? (
        <Box sx={{ ml: rowNode.depth * 4 }}>
          <Stack direction="row" sx={{ position: "relative", py: 2, alignItems: "start", mr: 2 }}>
            <IconButton
              aria-label={t(isExpanded ? "Common.Collapse" : "Common.Expand")}
              sx={{ marginRight: "6px", position: "absolute", top: 10, left: -30, mixBlendMode: "multiply" }}
              size="small"
              onClick={handleClick}
            >
              <ChevronRightIcon rotate={isExpanded ? 90 : 0} size={14} />
            </IconButton>

            <Stack direction="column">
              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }} variant="h4">
                {apiRef.current.getCellValue(id, field)}
              </Typography>
              <Typography
                sx={{
                  ...multilineEllipsis(1),
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.secondary,
                }}
              >
                {row.customizedFields?.questionText ?? row.questionText}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Stack direction="column" sx={{ ml: rowNode.depth * 4 }}>
          <Typography variant="h4" sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>
            {apiRef.current.getCellValue(id, field)}
          </Typography>
          <Typography
            sx={{
              ...multilineEllipsis(1),
              fontSize: theme => theme.typography.pxToRem(12),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {row.customizedFields?.questionText ?? row.questionText}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
